import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { AxiosError } from "axios";
import { NJIM, glassCardSx, pageShellSx, primaryBtnSx } from "../../../theme/njimTheme";
import { useTranslation } from "react-i18next";
import salesManagerEnrolmentService from "../../../services/SalesManagerEnrolmentService";
import loginService from "../../../services/LoginServices";
import {
  SalesManagerProfile,
  SalesManagerRepSummary,
  SalesManagerSyncResult,
  SalesManagerSyncStatus,
} from "../../../types/salesManagerEnrolment";
import { showToast } from "../../component/common/toastNotification";
import CustomInput from "../../component/common/inputField";
import {
  identityPasswordPolicyMessage,
  localizeEnrolmentRole,
  localizeEnrolmentSyncDescription,
  localizeEnrolmentSyncStatus,
} from "../../../helper/enrolmentI18n";

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const axiosErr = error as AxiosError;
  const data = axiosErr.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (Array.isArray(data) && data.length > 0) return data.map(String).join(". ");
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (typeof obj.status === "string" && obj.status.trim()) return obj.status.trim();
    if (typeof obj.message === "string") return obj.message;
    if (Array.isArray(obj.errors)) return obj.errors.map(String).join(". ");
  }
  return fallback;
};

const isSyncedStatus = (status?: string) =>
  status === "Synced" || status === "In System (Synced)";

const SalesManagerEnrolment: React.FC = () => {
  const { t } = useTranslation();
  const [managerCodeSearch, setManagerCodeSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [profile, setProfile] = useState<SalesManagerProfile | null>(null);
  const [syncStatus, setSyncStatus] = useState<SalesManagerSyncStatus | null>(null);
  const [syncResult, setSyncResult] = useState<SalesManagerSyncResult | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resetLoginUsername, setResetLoginUsername] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetPwConfirm, setShowResetPwConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [showManualRegister, setShowManualRegister] = useState(false);
  const [userName, setUserName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  const portalUsername =
    syncResult?.userName ||
    syncStatus?.userName ||
    profile?.portalAccount.userName ||
    resetLoginUsername ||
    profile?.salesManagerCode ||
    "";

  const isSynced =
    isSyncedStatus(syncStatus?.status) || Boolean(profile?.portalAccount.exists);

  const resetPolicyError = resetPassword ? identityPasswordPolicyMessage(resetPassword, t) : null;
  const resetPasswordsMatch =
    Boolean(resetPassword) &&
    Boolean(resetPasswordConfirm) &&
    resetPassword === resetPasswordConfirm;
  const canResetPassword =
    Boolean(portalUsername.trim()) &&
    resetPasswordsMatch &&
    !resetPolicyError &&
    !resetLoading;

  const passwordPolicyError = password ? identityPasswordPolicyMessage(password, t) : null;
  const passwordsMatch =
    Boolean(password) && Boolean(passwordConfirm) && password === passwordConfirm;
  const canRegister =
    Boolean(profile?.salesManagerCode) &&
    Boolean(userName.trim()) &&
    Boolean(firstName.trim()) &&
    Boolean(lastName.trim()) &&
    passwordsMatch &&
    !passwordPolicyError &&
    !registerLoading;

  const loadManager = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setSearchLoading(true);
    setNotFound(false);
    setProfile(null);
    setSyncStatus(null);
    setSyncResult(null);

    try {
      const profileData = await salesManagerEnrolmentService.getProfile(trimmed);
      const statusData = await salesManagerEnrolmentService.getSyncStatus(trimmed, profileData);

      setProfile(profileData);
      setSyncStatus(statusData);
      setResetLoginUsername(
        statusData.userName ||
          profileData.portalAccount.userName ||
          profileData.salesManagerCode
      );

      const nameParts = profileData.salesManagerName.trim().split(/\s+/);
      setFirstName(nameParts[0] ?? "");
      setLastName(nameParts.slice(1).join(" "));
      if (!userName.trim()) setUserName(profileData.salesManagerCode);

      const usingListFallback =
        profileData.assignedReps.length === 0 && !profileData.portalAccount.exists;

      showToast(
        usingListFallback
          ? t("salesManagerEnrolment.managerLoadedPartial", {
              defaultValue:
                "Manager found. Deploy the latest Main API for full profile, assigned reps, and sync.",
            })
          : t("salesManagerEnrolment.managerLoaded", {
              defaultValue: "Sales manager details loaded.",
            }),
        "success"
      );
    } catch (error) {
      const axiosErr = error as AxiosError;
      if (axiosErr.response?.status === 404) {
        setNotFound(true);
      }
      const fallback =
        axiosErr.response?.status === 401
          ? t("salesManagerEnrolment.sessionExpired", {
              defaultValue: "Session expired. Please log in again as Admin.",
            })
          : t("salesManagerEnrolment.managerNotFound", {
              defaultValue: "Sales manager not found in Sage sync.",
            });
      showToast(extractApiErrorMessage(error, fallback), "error");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = () => {
    if (!managerCodeSearch.trim()) {
      showToast(
        t("salesManagerEnrolment.enterManagerCode", { defaultValue: "Enter a sales manager code." }),
        "error"
      );
      return;
    }
    loadManager(managerCodeSearch);
  };

  const handleSync = async () => {
    if (!profile?.salesManagerCode) return;
    try {
      setSyncLoading(true);
      const result = await salesManagerEnrolmentService.syncManagerNormalized(
        profile.salesManagerCode
      );
      setSyncResult(result);
      setSyncStatus({
        status: result.status,
        description: result.description,
        userName: result.userName,
      });
      if (result.userName) setResetLoginUsername(result.userName);

      setProfile((prev) =>
        prev
          ? {
              ...prev,
              portalAccount: {
                exists: true,
                userName: result.userName || prev.portalAccount.userName,
                mustChangePassword:
                  result.mustChangePassword ?? prev.portalAccount.mustChangePassword,
              },
            }
          : prev
      );

      showToast(
        t("salesManagerEnrolment.syncSuccess", {
          defaultValue: "Sales manager portal account synced.",
        }),
        "success"
      );

      await loadManager(profile.salesManagerCode);
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          t("salesManagerEnrolment.syncFailed", {
            defaultValue: "Failed to sync sales manager account.",
          })
        ),
        "error"
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!canResetPassword) return;
    try {
      setResetLoading(true);
      await loginService.resetUserPassword(portalUsername.trim(), resetPassword);
      showToast(
        t("salesManagerEnrolment.passwordResetSuccess", {
          user: portalUsername,
        }),
        "success"
      );
      setResetPassword("");
      setResetPasswordConfirm("");
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          t("salesManagerEnrolment.passwordResetFailed", {
            defaultValue: "Failed to reset password. Sync the manager account first.",
          })
        ),
        "error"
      );
    } finally {
      setResetLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!canRegister || !profile) return;
    try {
      setRegisterLoading(true);
      await salesManagerEnrolmentService.registerSalesManager({
        userName: userName.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        salesManagerCode: profile.salesManagerCode,
      });
      setResetLoginUsername(userName.trim());
      showToast(
        t("salesManagerEnrolment.accountCreated", {
          defaultValue: "Sales manager account created successfully.",
        }),
        "success"
      );
      await loadManager(profile.salesManagerCode);
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          t("salesManagerEnrolment.registerFailed", {
            defaultValue: "Failed to create sales manager account.",
          })
        ),
        "error"
      );
    } finally {
      setRegisterLoading(false);
    }
  };

  const renderRepsTable = (rows: SalesManagerRepSummary[]) => (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary, fontSize: "14px" }}>
          {t("salesManagerEnrolment.assignedReps", { defaultValue: "Assigned Sales Reps" })}
        </Typography>
        <Chip size="small" label={rows.length} sx={{ height: 22, fontWeight: 700 }} />
      </Stack>
      <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, mb: 1 }}>
        {t("salesManagerEnrolment.assignedRepsHint", {
          defaultValue:
            "Sales reps supervised by this manager (from synced Sage data). The manager sees only these reps' COD orders.",
        })}
      </Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary, fontStyle: "italic" }}>
          {t("salesManagerEnrolment.noRepsAssigned", {
            defaultValue: "No sales reps assigned to this manager.",
          })}
        </Typography>
      ) : (
        <TableContainer sx={{ borderRadius: 2, border: "1px solid rgba(148,163,184,0.22)", maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: "rgba(241,245,249,0.95)" }}>
                  {t("salesManagerEnrolment.repCode", { defaultValue: "Rep Code" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: "rgba(241,245,249,0.95)" }}>
                  {t("salesManagerEnrolment.repName", { defaultValue: "Rep Name" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: "rgba(241,245,249,0.95)" }}>
                  {t("salesManagerEnrolment.repRole", { defaultValue: "Rep Role" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.salesRepCode}>
                  <TableCell>{row.salesRepCode}</TableCell>
                  <TableCell>{row.salesRepName}</TableCell>
                  <TableCell>{localizeEnrolmentRole(row.repRole, t)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <Box sx={pageShellSx} py={3}>
      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 1, sm: 2, md: 0 } }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: NJIM.textPrimary, mb: 0.5 }}>
          {t("salesManagerEnrolment.title", { defaultValue: "Sales Manager Enrolment" })}
        </Typography>
        <Typography sx={{ color: NJIM.textSecondary, mb: 3 }}>
          {t("salesManagerEnrolment.subtitle", {
            defaultValue:
              "Search a sales manager by Sage code, sync portal accounts, and review supervised reps.",
          })}
        </Typography>

        <Card sx={glassCardSx}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background: NJIM.mintWash,
                border: "1px solid rgba(93,183,167,0.25)",
                color: NJIM.ink,
              }}
            >
              <SupervisorAccountOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
              {t("salesManagerEnrolment.searchManager", { defaultValue: "Search Sales Manager" })}
            </Typography>
          </Stack>

          <Stack
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "flex-end" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomInput
                name="managerCodeSearch"
                label={t("salesManagerEnrolment.managerCodeLabel", {
                  defaultValue: "Sales Manager Code",
                })}
                placeholder={t("salesManagerEnrolment.managerCodePlaceholder", {
                  defaultValue: "e.g. 007",
                })}
                value={managerCodeSearch}
                onChange={(e) => setManagerCodeSearch(e.target.value)}
                disabled={searchLoading}
                marginBottom="0px"
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={searchLoading || !managerCodeSearch.trim()}
              startIcon={
                searchLoading ? <CircularProgress size={16} sx={{ color: NJIM.white }} /> : <SearchIcon />
              }
              sx={{
                ...primaryBtnSx,
                minWidth: 140,
              }}
            >
              {t("salesManagerEnrolment.searchBtn", { defaultValue: "Search" })}
            </Button>
          </Stack>

          {notFound && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "rgba(239,68,68,0.06)",
                border: "1px solid rgba(239,68,68,0.22)",
              }}
            >
              <Typography sx={{ color: NJIM.danger, fontWeight: 700, fontSize: "14px" }}>
                {t("salesManagerEnrolment.managerNotFoundTitle", {
                  defaultValue: "Manager code not found in Sage",
                })}
              </Typography>
            </Box>
          )}
        </Card>

        {profile && (
          <>
            {syncResult?.tempPassword && (
              <Card
                sx={{
                  ...glassCardSx,
                  background:
                    "linear-gradient(135deg, rgba(34,197,94,0.10), rgba(255,255,255,0.62))",
                  border: "1px solid rgba(34,197,94,0.18)",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircleOutlinedIcon sx={{ color: NJIM.success, mt: 0.3 }} />
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {t("salesManagerEnrolment.syncCredentials", {
                      defaultValue:
                        "Portal account created. Username: {{user}} — Temporary password: {{pw}}",
                      user: syncResult.userName || profile.salesManagerCode,
                      pw: syncResult.tempPassword,
                    })}
                  </Typography>
                </Stack>
              </Card>
            )}

            <Card sx={glassCardSx}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                <Box
                  sx={{
                    width: 45,
                    height: 45,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: isSynced ? NJIM.mintWash : "rgba(245,158,11,0.14)",
                    color: isSynced ? NJIM.ink : NJIM.warning,
                    border: "1px solid rgba(148,163,184,0.22)",
                  }}
                >
                  {isSynced ? (
                    <CheckCircleOutlinedIcon />
                  ) : syncStatus?.status === "Not found" ? (
                    <CancelOutlinedIcon />
                  ) : (
                    <ReportProblemOutlinedIcon />
                  )}
                </Box>
                <Box flex={1}>
                  <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                    {t("salesManagerEnrolment.syncStatus", { defaultValue: "Portal Sync Status" })}{" "}
                    <Chip
                      label={localizeEnrolmentSyncStatus(syncStatus?.status, t)}
                      size="small"
                      sx={{ marginInlineStart: 1, fontWeight: 800, bgcolor: NJIM.mintWash, color: NJIM.ink }}
                    />
                  </Typography>
                  <Typography color={NJIM.textSecondary} mt={0.75}>
                    {localizeEnrolmentSyncDescription(syncStatus?.description, t)}
                  </Typography>
                  {isSynced && portalUsername && (
                    <Typography color={NJIM.textSecondary} mt={0.5} sx={{ fontSize: "13px" }}>
                      {t("salesManagerEnrolment.portalLogin", { defaultValue: "Portal login:" })}{" "}
                      <strong>{portalUsername}</strong>
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={
                    syncLoading ? <CircularProgress size={16} sx={{ color: NJIM.white }} /> : <SyncIcon />
                  }
                  onClick={handleSync}
                  disabled={syncLoading}
                  sx={{
                    ...primaryBtnSx,
                    whiteSpace: "nowrap",
                  }}
                >
                  {isSynced
                    ? t("salesManagerEnrolment.resyncBtn", { defaultValue: "Re-sync Account" })
                    : t("salesManagerEnrolment.syncBtn", { defaultValue: "Sync & Create Account" })}
                </Button>
              </Stack>
            </Card>

            <Card sx={glassCardSx}>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                    display: "grid",
                    placeItems: "center",
                    background: "rgba(34,197,94,0.12)",
                    border: "1px solid rgba(34,197,94,0.18)",
                    color: NJIM.success,
                  }}
                >
                  <GroupOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                  {t("salesManagerEnrolment.managerDetails", {
                    defaultValue: "Manager Details (from Sage)",
                  })}
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                {[
                  [t("salesManagerEnrolment.managerCode"), profile.salesManagerCode],
                  [t("salesManagerEnrolment.managerName"), profile.salesManagerName || "—"],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={String(label)}>
                    <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 600 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: NJIM.textPrimary }}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>

              {renderRepsTable(profile.assignedReps)}
            </Card>

            <Card
              sx={{
                ...glassCardSx,
                border: "1px solid rgba(245,158,11,0.22)",
                background: "linear-gradient(135deg, rgba(245,158,11,0.06), rgba(255,255,255,0.66))",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <LockResetIcon sx={{ color: NJIM.warning }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {t("salesManagerEnrolment.resetPassword", { defaultValue: "Reset Password" })}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary }}>
                    {t("salesManagerEnrolment.resetPasswordDesc", {
                      defaultValue:
                        "Set a temporary password (min 6 chars, upper + lower + digit + special). Manager changes it on next login.",
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("salesManagerEnrolment.loginUsername", {
                      defaultValue: "Portal login username",
                    })}
                    value={resetLoginUsername || portalUsername}
                    onChange={(e) => setResetLoginUsername(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type={showResetPw ? "text" : "password"}
                    label={t("salesManagerEnrolment.newPassword", { defaultValue: "New Password" })}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowResetPw((p) => !p)}>
                            {showResetPw ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type={showResetPwConfirm ? "text" : "password"}
                    label={t("salesManagerEnrolment.confirmNewPassword", {
                      defaultValue: "Confirm Password",
                    })}
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowResetPwConfirm((p) => !p)}>
                            {showResetPwConfirm ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleResetPassword}
                    disabled={!canResetPassword}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${NJIM.warning}, ${NJIM.warning})`,
                    }}
                  >
                    {resetLoading ? (
                      <CircularProgress size={18} sx={{ color: NJIM.white }} />
                    ) : (
                      t("salesManagerEnrolment.resetBtn", { defaultValue: "Reset" })
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Card>

            <Card sx={glassCardSx}>
              <Button
                onClick={() => setShowManualRegister((v) => !v)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: NJIM.ink,
                  mb: showManualRegister ? 2 : 0,
                }}
              >
                {showManualRegister
                  ? t("salesManagerEnrolment.hideManualRegister", {
                      defaultValue: "Hide custom username registration",
                    })
                  : t("salesManagerEnrolment.showManualRegister", {
                      defaultValue: "Advanced: register with a custom username",
                    })}
              </Button>
              <Collapse in={showManualRegister}>
                <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, mb: 2 }}>
                  {t("salesManagerEnrolment.manualRegisterHint", {
                    defaultValue:
                      "Sync uses the manager code as username (e.g. 007). Use this only if you need a different login name.",
                  })}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("salesManagerEnrolment.username")}
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("salesManagerEnrolment.firstName")}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t("salesManagerEnrolment.lastName")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type={showPassword ? "text" : "password"}
                      label={t("salesManagerEnrolment.password")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPassword((p) => !p)}>
                              {showPassword ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type={showPasswordConfirm ? "text" : "password"}
                      label={t("salesManagerEnrolment.confirmPassword")}
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPasswordConfirm((p) => !p)}
                            >
                              {showPasswordConfirm ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="outlined"
                  onClick={handleRegister}
                  disabled={!canRegister}
                  sx={{ mt: 2, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                >
                  {registerLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    t("salesManagerEnrolment.createAccountBtn")
                  )}
                </Button>
              </Collapse>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SalesManagerEnrolment;
